import React, { useEffect, useState, useCallback } from "react";
import {
  Check, X, RefreshCw, Edit3, Loader2, Mail as MailIcon, Twitter, Linkedin, Instagram,
  Sparkles, Settings as SettingsIcon, Save, Inbox, Archive
} from "lucide-react";
import { approvalsApi, voiceProfileApi } from "../api";

const platMeta = (id) => {
  if (id === "hexa-mail" || id === "mail") return { label: "HEXA Mail", Icon: MailIcon, color: "#a78bfa" };
  if (id === "twitter") return { label: "Twitter / X", Icon: Twitter, color: "#8b5cf6" };
  if (id === "linkedin") return { label: "LinkedIn", Icon: Linkedin, color: "#a78bfa" };
  if (id === "instagram") return { label: "Instagram", Icon: Instagram, color: "#f472b6" };
  return { label: id, Icon: Sparkles, color: "#c7c1ff" };
};

const kindLabel = (k) => ({ mail: "Email Reply", post: "Social Post", dm: "DM Reply" }[k] || k);

function ContextSummary({ a }) {
  if (a.kind === "mail") {
    return (
      <div className="mono text-[10px] text-[#c7c1ff]/55">
        → To: <span className="text-[#3b82f6]">{a.context?.to_addr}</span> &nbsp;·&nbsp;
        Subject: <span className="text-[#c7c1ff]">{a.context?.subject}</span>
      </div>
    );
  }
  if (a.kind === "dm") {
    return (
      <div className="mono text-[10px] text-[#c7c1ff]/55">
        → DM to <span className="text-[#3b82f6]">{a.context?.contact_name} ({a.context?.contact_handle})</span>
      </div>
    );
  }
  if (a.kind === "post") {
    return (
      <div className="mono text-[10px] text-[#c7c1ff]/55">
        → Topic: <span className="text-[#c7c1ff]">{a.context?.topic}</span>
      </div>
    );
  }
  return null;
}

function ApprovalCard({ a, onUpdate }) {
  const [body, setBody] = useState(a.draft_body);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const meta = platMeta(a.platform);
  const { Icon } = meta;

  useEffect(() => { setBody(a.draft_body); }, [a.draft_body]);

  const save = async () => {
    setBusy(true);
    try {
      const updated = await approvalsApi.edit(a.id, body);
      onUpdate?.(updated);
      setEditing(false);
    } catch { /* ignore */ }
    setBusy(false);
  };
  const approve = async () => {
    setBusy(true);
    try { await approvalsApi.approve(a.id); onUpdate?.({ ...a, status: "approved" }); } catch { /* ignore */ }
    setBusy(false);
  };
  const reject = async () => {
    setBusy(true);
    try { await approvalsApi.reject(a.id); onUpdate?.({ ...a, status: "rejected" }); } catch { /* ignore */ }
    setBusy(false);
  };
  const regen = async () => {
    setBusy(true);
    try {
      const updated = await approvalsApi.regenerate(a.id);
      onUpdate?.(updated);
      setBody(updated.draft_body);
    } catch { /* ignore */ }
    setBusy(false);
  };

  return (
    <div
      data-testid={`approval-card-${a.id}`}
      className="panel p-4 space-y-3"
      style={{
        clipPath: "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))",
      }}
    >
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest" style={{ color: meta.color }}>
          <Icon size={12} /> {meta.label}
        </span>
        <span className="mono text-[9px] uppercase tracking-widest px-2 py-0.5" style={{ background: "rgba(59,130,246,0.12)", color: "#3b82f6", border: "1px solid rgba(59,130,246,0.3)" }}>
          {kindLabel(a.kind)}
        </span>
        <span className="ml-auto mono text-[9px] text-[#c7c1ff]/40">{new Date(a.created).toLocaleString()}</span>
      </div>

      <ContextSummary a={a} />

      {editing ? (
        <textarea
          data-testid={`approval-textarea-${a.id}`}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={5}
          className="w-full bg-[#0e1a22] border border-[#8b5cf6]/30 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/70"
        />
      ) : (
        <div className="bg-[#0e1a22] border border-[#8b5cf6]/15 p-3 text-sm whitespace-pre-wrap text-[#e8f4f8] min-h-[60px]">
          {body}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <button
          data-testid={`approval-approve-${a.id}`}
          onClick={approve}
          disabled={busy}
          className="btn-accent flex items-center gap-1.5 text-[11px] uppercase tracking-widest mono"
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Check size={12} />} Approve & Send
        </button>
        {editing ? (
          <button
            data-testid={`approval-save-${a.id}`}
            onClick={save}
            disabled={busy}
            className="btn-ghost flex items-center gap-1.5 text-[11px] uppercase tracking-widest mono"
          >
            <Save size={11} /> Save Edit
          </button>
        ) : (
          <button
            data-testid={`approval-edit-${a.id}`}
            onClick={() => setEditing(true)}
            disabled={busy}
            className="btn-ghost flex items-center gap-1.5 text-[11px] uppercase tracking-widest mono"
          >
            <Edit3 size={11} /> Edit
          </button>
        )}
        <button
          data-testid={`approval-regen-${a.id}`}
          onClick={regen}
          disabled={busy}
          className="btn-ghost flex items-center gap-1.5 text-[11px] uppercase tracking-widest mono"
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Regenerate
        </button>
        <button
          data-testid={`approval-reject-${a.id}`}
          onClick={reject}
          disabled={busy}
          className="btn-ghost flex items-center gap-1.5 text-[11px] uppercase tracking-widest mono text-[#f87171]"
        >
          <X size={11} /> Discard
        </button>
      </div>
    </div>
  );
}

function VoiceProfilePanel() {
  const [vp, setVp] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { voiceProfileApi.get().then(setVp); }, []);

  const save = async () => {
    if (!vp) return;
    const r = await voiceProfileApi.update({
      tone: vp.tone,
      style: vp.style,
      signature: vp.signature,
      do_not_say: vp.do_not_say,
    });
    setVp(r);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (!vp) return null;

  const Field = ({ label, k, placeholder, ml }) => (
    <div>
      <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/60 mb-1">{label}</div>
      {ml ? (
        <textarea
          data-testid={`vp-${k}`}
          value={vp[k] || ""}
          onChange={(e) => setVp({ ...vp, [k]: e.target.value })}
          placeholder={placeholder}
          rows={2}
          className="w-full bg-[#0e1a22] border border-[#8b5cf6]/20 px-2 py-1.5 text-xs outline-none focus:border-[#8b5cf6]/60"
        />
      ) : (
        <input
          data-testid={`vp-${k}`}
          value={vp[k] || ""}
          onChange={(e) => setVp({ ...vp, [k]: e.target.value })}
          placeholder={placeholder}
          className="w-full bg-[#0e1a22] border border-[#8b5cf6]/20 px-2 py-1.5 text-xs outline-none focus:border-[#8b5cf6]/60 mono"
        />
      )}
    </div>
  );

  return (
    <div className="panel p-4 space-y-3">
      <div className="flex items-center gap-2">
        <SettingsIcon size={13} color="#3b82f6" />
        <div className="mono text-[11px] uppercase tracking-widest text-[#3b82f6]">// voice profile</div>
        <span className="mono text-[9px] text-[#c7c1ff]/40 ml-2">applied to every AI draft</span>
      </div>
      <Field label="tone" k="tone" placeholder="professional yet warm" />
      <Field label="style" k="style" placeholder="concise, direct, occasionally witty" />
      <Field label="signature line (optional)" k="signature" placeholder="— Alex, HEXA Team" />
      <Field label="don't mention" k="do_not_say" placeholder="competitor names, pricing, ETA" ml />
      <button
        data-testid="vp-save"
        onClick={save}
        className="btn-accent flex items-center gap-1.5 text-[11px] uppercase tracking-widest mono"
      >
        <Save size={11} /> {saved ? "saved" : "save profile"}
      </button>
    </div>
  );
}

export default function Approvals() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [showProfile, setShowProfile] = useState(false);

  const load = useCallback(async (status = filter) => {
    setLoading(true);
    try { setItems(await approvalsApi.list(status)); } catch { /* ignore */ }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(filter); }, [filter, load]);

  // refresh every 8s while open so new drafts from Mail/Social bubble in
  useEffect(() => {
    const t = setInterval(() => { if (filter === "pending") load("pending"); }, 8000);
    return () => clearInterval(t);
  }, [filter, load]);

  const stats = items.reduce((acc, it) => {
    acc[it.kind] = (acc[it.kind] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="h-full flex flex-col" style={{ background: "#ffffff" }}>
      <div className="border-b border-[#8b5cf6]/15 px-3 flex items-center">
        {[
          { id: "pending", label: "Pending", Icon: Inbox },
          { id: "approved", label: "Approved", Icon: Check },
          { id: "rejected", label: "Discarded", Icon: Archive },
        ].map(t => {
          const { Icon } = t;
          const active = filter === t.id;
          return (
            <button
              key={t.id}
              data-testid={`approval-filter-${t.id}`}
              onClick={() => setFilter(t.id)}
              className={`px-4 py-2 mono text-[11px] uppercase tracking-widest flex items-center gap-2 ${active ? "text-[#3b82f6]" : "text-[#c7c1ff]/60 hover:text-[#8b5cf6]"}`}
              style={{ borderBottom: active ? "2px solid #3b82f6" : "2px solid transparent" }}
            >
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-3 pr-2">
          <span className="mono text-[9px] text-[#c7c1ff]/50 uppercase tracking-widest">
            mail:{stats.mail || 0} · post:{stats.post || 0} · dm:{stats.dm || 0}
          </span>
          <button
            data-testid="approvals-toggle-profile"
            onClick={() => setShowProfile(s => !s)}
            className={`mono text-[10px] uppercase tracking-widest flex items-center gap-1 ${showProfile ? "text-[#3b82f6]" : "text-[#c7c1ff]/60 hover:text-[#8b5cf6]"}`}
          >
            <SettingsIcon size={11} /> voice profile
          </button>
          <button
            onClick={() => load(filter)}
            className="text-[#c7c1ff]/60 hover:text-[#8b5cf6]"
            title="Refresh"
            data-testid="approvals-refresh"
          >
            <RefreshCw size={13} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {showProfile && <VoiceProfilePanel />}

        {loading && items.length === 0 ? (
          <div className="text-xs mono text-[#c7c1ff]/50">loading…</div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <span
              className="inline-block mb-4"
              style={{
                width: 56, height: 64,
                clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                background: "linear-gradient(160deg, rgba(139,92,246,0.2), rgba(139,92,246,0.05))",
                border: "1px solid rgba(139,92,246,0.3)",
              }}
            />
            <div className="mono text-xs text-[#c7c1ff]/60 uppercase tracking-widest">// nothing {filter}</div>
            <div className="mono text-[10px] text-[#c7c1ff]/40 mt-1">
              {filter === "pending"
                ? "use AI Draft inside Mail or Social to queue a draft for approval"
                : `no ${filter} drafts to show`}
            </div>
          </div>
        ) : (
          items.map(a => (
            <ApprovalCard
              key={a.id}
              a={a}
              onUpdate={(upd) => {
                if (upd && upd.id === a.id && (upd.status === "approved" || upd.status === "rejected")) {
                  // drop from pending list
                  if (filter === "pending") setItems(items.filter(x => x.id !== a.id));
                  else setItems(items.map(x => x.id === a.id ? upd : x));
                } else if (upd) {
                  setItems(items.map(x => x.id === a.id ? upd : x));
                }
              }}
            />
          ))
        )}
      </div>
    </div>
  );
}
